import { clsx } from "clsx";
import { useEffect, useState } from "react";
import type { z } from "zod/v4";
import { Section } from "@/components/Section";
import type { LeadMagnetNextStepsPropsSchema } from "@/schemas/sections";
import type { PostSubmitContext } from "@/types/config";
import type { SectionWrapperProps } from "@/types/section-wrapper";
import { trackLeadMagnetNext } from "@/utils/tracking";
import {
  contextFromPostSubmit,
  isWebinarReferrer,
  type PageContext,
  readPageContext,
  renderSubtitle,
  resolveContent,
} from "./content-resolution";
import { DeliveryNote } from "./DeliveryNote";
import { EventCard } from "./EventCard";
import { NextStepsCards } from "./NextStepsCards";
import { NextStepsList } from "./NextStepsList";
import { OnboardingStep } from "./OnboardingStep";
import { PageHeading } from "./PageHeading";
import { TelegramConnectBlock, useConnectGate } from "./TelegramConnectGate";
import { TONE_PALETTE_VARS } from "./tone-tokens";

export type LeadMagnetNextStepsSectionProps = z.input<typeof LeadMagnetNextStepsPropsSchema> &
  SectionWrapperProps & {
    // SSR-resolved post-submit context (email/source/kind) threaded from
    // +onBeforeRender via pageProps → renderSection, so the correct copy variant
    // is in the initial HTML (no post-hydration flash).
    postSubmit?: PostSubmitContext | null;
  };

function usePostSubmitContext(
  postSubmit: PostSubmitContext | null | undefined,
  mode: string,
): PageContext {
  const [ctx, setCtx] = useState<PageContext>(() => contextFromPostSubmit(postSubmit));
  useEffect(() => {
    const server = contextFromPostSubmit(postSubmit);
    const hasServer = server.email || server.source || server.kind || server.connectTokenAvailable;
    let next = hasServer ? server : readPageContext();
    if (!next.kind && isWebinarReferrer()) next = { ...next, kind: "webinar" };
    setCtx(next);
    trackLeadMagnetNext(next.source, mode);
  }, [mode, postSubmit]);
  return ctx;
}

export function LeadMagnetNextSteps({
  mode = "delivered",
  title,
  subtitle,
  subtitleNoEmail,
  spamTitle = "Письмо не пришло за пару минут?",
  spamNote,
  steps = [],
  supportEmail = "info@lenacareer.ru",
  nextTitle = "Что дальше?",
  nextSubtitle,
  telegramConnect,
  maxConnect,
  onboarding,
  event,
  nextStepsLayout = "cards",
  cards = [],
  byKind,
  bySource,
  postSubmit,
  id,
  background = "base-100",
  padding = "lg",
  className,
}: LeadMagnetNextStepsSectionProps) {
  const ctx = usePostSubmitContext(postSubmit, mode);
  const {
    title: rTitle,
    subtitle: rSubtitle,
    subtitleNoEmail: rSubtitleNoEmail,
    spamTitle: rSpamTitle,
    spamNote: rSpamNote,
    steps: rSteps,
    nextTitle: resolvedNextTitle,
    nextSubtitle: resolvedNextSubtitle,
    telegramConnect: resolvedTelegramConnect,
    maxConnect: resolvedMaxConnect,
    onboarding: resolvedOnboarding,
    event: resolvedEvent,
    nextStepsLayout: resolvedLayout,
    cards: resolvedCards,
  } = resolveContent(
    {
      title,
      subtitle,
      subtitleNoEmail,
      spamTitle,
      spamNote,
      steps,
      nextTitle,
      nextSubtitle,
      telegramConnect,
      maxConnect,
      onboarding,
      event,
      nextStepsLayout,
      cards,
    },
    ctx.kind ? byKind?.[ctx.kind] : undefined,
    ctx.source ? bySource?.[ctx.source] : undefined,
  );

  // An already-connected visitor (`botConnected`, resolved SSR-side from the `?t=`
  // token) never sees the connect module — no re-link CTA. With the block off,
  // `useConnectGate(undefined)` is inactive → `revealed` is true → the rest of
  // the page (onboarding + upsell cards) shows straight away.
  const connectEnabled =
    !!resolvedTelegramConnect && resolvedTelegramConnect.enabled !== false && !ctx.botConnected;
  const gate = useConnectGate(connectEnabled ? resolvedTelegramConnect : undefined);
  const showOnboarding =
    gate.revealed && !!resolvedOnboarding && resolvedOnboarding.enabled !== false;
  const hasEvent = !!resolvedEvent;

  return (
    <Section
      id={id}
      background={background}
      padding={padding}
      // While the gate is closed the section fills the viewport, so the footer
      // sits below the fold and the connect choices stay the primary action.
      // `svh` (not `vh`) — mobile browser chrome makes `vh` overshoot and push
      // the CTA itself out of view on exactly our 75%-mobile traffic.
      // `grid`+`content-center` rather than flex: ADR-0094 routes flex layout
      // through the Flex primitive, and Section's `containerLayout` only reaches
      // the inner container — the min-height has to live on the section itself.
      className={clsx(!gate.revealed && "grid min-h-svh content-center", className)}
      data-lm-source={ctx.source || undefined}
    >
      <div
        data-component="LeadMagnetNextSteps"
        className="mx-auto w-full max-w-3xl"
        style={TONE_PALETTE_VARS}
      >
        <PageHeading
          mode={mode}
          title={rTitle}
          subtitle={hasEvent ? undefined : renderSubtitle(rSubtitle, ctx.email, rSubtitleNoEmail)}
          hasEvent={hasEvent}
        />

        {resolvedEvent && <EventCard cfg={resolvedEvent} />}

        {connectEnabled && resolvedTelegramConnect && (
          <TelegramConnectBlock
            cfg={resolvedTelegramConnect}
            gate={gate}
            maxConnect={resolvedMaxConnect}
            onMaxTapped={gate.markMaxTapped}
          />
        )}

        {/* Both layouts stay behind the same ADR-0234 gate — `list` is a visual
            variant of the post-connect step, never a way around it. */}
        {resolvedLayout === "list" ? (
          gate.revealed && (
            <NextStepsList
              heading={resolvedNextTitle}
              subheading={resolvedNextSubtitle}
              onboarding={resolvedOnboarding}
              cards={resolvedCards}
            />
          )
        ) : (
          <>
            {showOnboarding && resolvedOnboarding && <OnboardingStep cfg={resolvedOnboarding} />}
            {gate.revealed && (
              <NextStepsCards
                heading={resolvedNextTitle}
                subheading={resolvedNextSubtitle}
                cards={resolvedCards}
              />
            )}
          </>
        )}

        <DeliveryNote
          // Compact only when there is nothing to lose: the compact branch has
          // no room for the numbered `steps` of `mode: "confirm"`, so a config
          // combining an event card with steps keeps the boxed panel rather
          // than silently dropping them.
          compact={!!resolvedEvent && rSteps.length === 0}
          subtitle={renderSubtitle(rSubtitle, ctx.email, rSubtitleNoEmail)}
          spamTitle={rSpamTitle}
          spamNote={rSpamNote}
          steps={rSteps}
          supportEmail={supportEmail}
        />
      </div>
    </Section>
  );
}

LeadMagnetNextSteps.displayName = "LeadMagnetNextSteps";
